import apiClient from './apiClient';

export const getHomePageNovels = async () => {
  const response = await apiClient.get('/api/novels/homepage');
  return response.data;
};

export const getNovels = async (params = undefined) => {
  const response = await apiClient.get('/api/novels', {
    params,
  });
  return response.data;
};

export const getNovelsBatch = async (novelIds) => {
  if (!novelIds || novelIds.length === 0) {
    return [];
  }
  const response = await apiClient.post('/api/novels/batch', novelIds);
  return response.data;
};

export const getNewArrivals = async () => {
  const response = await apiClient.get('/api/novels', {
    params: { sort: 'createdAt,DESC', size: 10 },
  });
  return response.data.content;
};

export const getTopRated = async () => {
  const response = await apiClient.get('/api/novels', {
    params: { sort: 'averageRating,DESC', size: 10 },
  });
  return response.data.content;
};

export const getCommunityFavorites = async () => {
  const response = await apiClient.get('/api/novels/community-favorites');
  return response.data;
};

export const getNovelById = async (id) => {
  const response = await apiClient.get(`/api/novels/${id}`);
  return response.data;
};

export const browseNovels = async (payload) => {
  const response = await apiClient.post('/api/novels/browse', payload);
  return response.data;
};

export const getRelatedNovels = async (id, { limit = 6 } = {}) => {
  const response = await apiClient.get(`/api/novels/${id}/related`, {
    params: { limit },
  });
  return response.data;
};

export const getNovelBySlug = async (slug) => {
  const response = await apiClient.get(`/api/novels/slug/${slug}`);
  return response.data;
};

export const getSimilarNovelsBySlug = async (slug, { limit = 12 } = {}) => {
  const response = await apiClient.get(`/api/novels/slug/${slug}/similar`, {
    params: { limit },
  });
  return response.data;
};

export const recordNovelView = async (novelId, payload = {}) => {
  const response = await apiClient.post(`/api/views/novel/${novelId}`, payload);
  return response.data;
};

export const searchNovels = async (title) => {
  const response = await apiClient.get('/api/novels/search', {
    params: { title },
  });
  return response.data;
};

export const getNovelsByAuthor = async (authorId, params = undefined) => {
  const response = await apiClient.get(`/api/novels/author/${authorId}`, {
    params,
  });
  return response.data;
};

export const getMyNovels = async (params = {}) => {
  const response = await apiClient.get('/api/novels/mine', { params });
  return response.data.content;
};

export const getNovelsByTags = async (tags) => {
  if (!tags || (Array.isArray(tags) && tags.length === 0)) {
    return [];
  }

  const normalized = Array.isArray(tags)
    ? tags.filter((slug) => typeof slug === 'string' && slug.trim().length > 0).join(',')
    : String(tags);

  if (!normalized) {
    return [];
  }

  const response = await apiClient.get('/api/novels', {
    params: { tags: normalized },
  });
  return response.data;
};

export const createNovel = async (payload) => {
  const response = await apiClient.post('/api/novels', payload);
  return response.data;
};

export const uploadNovelImage = async (novelId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  // 60s timeout — the backend may run gifsicle/ffmpeg on large animated
  // GIFs/WebPs (up to 30s for a 5 MB GIF). The default axios timeout (0 =
  // no limit) can cause the CRA dev proxy to hang instead of surfacing
  // the error cleanly.
  const response = await apiClient.post(`/api/novels/${novelId}/image`, formData, {
    timeout: 60000,
  });
  return response.data;
};

export const updateNovel = async (id, payload) => {
  const response = await apiClient.put(`/api/novels/${id}`, payload);
  return response.data;
};

export const getTakenDownNovels = async (page = 0, size = 20) => {
  const response = await apiClient.get('/api/novels/taken-down', { params: { page, size } });
  return response.data;
};

export const takedownNovel = async (id, reason) => {
  const response = await apiClient.put(`/api/novels/${id}/takedown`, { reason });
  return response.data;
};

export const restoreNovel = async (id) => {
  const response = await apiClient.put(`/api/novels/${id}/restore`);
  return response.data;
};

// Admin: move a taken-down novel into the 30-day soft-delete trash.
// Super-user only. The novel disappears from the takedown list immediately
// and can be restored from trash via restoreNovel (which clears deletedAt
// but leaves takenDown=true, so it reappears in the takedown list).
export const deleteTakenDownNovel = async (id) => {
  await apiClient.delete(`/api/novels/${id}/takedown`);
};

// Soft-delete (30-day grace period). Both authors and super-users soft-delete;
// the novel is hidden immediately and permanently purged by the cleanup scheduler
// after 30 days. There is no immediate hard-delete path for any user.
// `survey` = { reason, feedback } — the exit survey. Required (reason) for
// author deletes; super-user deletes may omit it.
export const deleteNovel = async (id, survey = {}) => {
  await apiClient.delete(`/api/novels/${id}`, { data: survey });
};

// Restore a soft-deleted novel from the author's Trash. Same endpoint as the
// admin takedown-restore — the backend branches by novel state and RBAC.
export const restoreDeletedNovel = async (id) => {
  const response = await apiClient.put(`/api/novels/${id}/restore`);
  return response.data;
};

// Paginated list of the authenticated author's soft-deleted novels (Trash UI).
export const getTrashedNovels = async (params = {}) => {
  const response = await apiClient.get('/api/novels/trash', { params });
  return response.data;
};

export const updateSupportLinks = async (id, supportLinks) => {
  const response = await apiClient.put(`/api/novels/${id}/support-links`, {
    supportLinks: JSON.stringify(supportLinks),
  });
  return response.data;
};

// ── Moderator-only: force fan-fiction flag ─────────────────────────────────
export const updateFanFictionStatus = async (id, isFanFiction, reason) => {
  const response = await apiClient.put(`/api/novels/admin/${id}/fan-fiction-status`, {
    isFanFiction,
    reason,
  });
  return response.data;
};

